// SPEC-18 · Dados gerais. Lena asks its questions in a conversation; each
// answer is stored on the device through the data layer the moment it is
// given, so leaving and coming back re-shows the history and asks the next
// question. At the end the profile goes to the server through saveProfile.
//
// A first run does not ask the first name typed in Criar conta: the run
// carries it, Lena greets her by it and asks the birth date first (the
// founder, 2026-10-15). A finished Dados gerais is edited, never redone
// (SPEC-16, answer 3c): the same conversation runs with her current values
// offered first, the name among them, only saveProfile is called, and the
// hub's "feito a" date stays.
//
// SPEC-27: opened from Conversa, which has just introduced Lena, it starts
// at « Prazer, {nome}. » with no second greeting (the founder's answer 3);
// ← and its end lead back to the tab it was opened from. An edit from
// Perfil goes back to Perfil.
//
// Which questions are asked, which is next, the counter, how a typed date
// reads and the minimum age are poweryourflow-lib's rules; this screen asks
// and shows.
//
// SPEC-31: Lena's lines, the fields and the buttons follow the app's
// language. Her answers are values and stay as she gave them: the sport
// and level chips, her load and the competition titles are PT in every
// language's copy.
//
// SPEC-55: after the position, Lena asks her strong side in her sport's
// words, with three quick replies and the composer open. A reply stores
// its option id, a typed answer her words; either goes with the kind of
// question that asked it. The question and the replies are worded in the
// app's language, the stored id never is.
//
// SPEC-54: an account the team created holds her Dados gerais before her
// first run. The conversation offers them as an edit does, without the
// name, and her birth date may then be from LIMITS.TEAM_MIN_AGE_YEARS: the
// `createdByTeam` claim of her token says so (Onboarding.createdByTeam).

import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import {
  COMPETITION_TYPES,
  DADOS,
  DADOS_ASKING_NAME,
  answersFromProfile,
  applyAnswer,
  dadosConversation,
  profileFromAnswers,
  progress,
  readBirthDate,
  resumeAt,
  screenIds,
} from "poweryourflow-lib";
import type { LateralityQuestion, Questionnaire } from "poweryourflow-lib";

import { PyfBubble, PyfTitle, TypingBubble, UserBubble } from "../components/Chat";
import { useCopy, useLanguage } from "../i18n/LanguageProvider";
import { originOf, originPath, waysOut } from "../lib/place";
import { paths, useRouter } from "../lib/router";
import type { Onboarding } from "../onboarding/onboarding";
import {
  answerLine,
  displayDate,
  firstNameOf,
  lateralityQuestionOf,
  localToday,
  questionLines,
  summaryRows,
} from "../onboarding/conversation";
import type { Asked } from "../onboarding/conversation";
import { lateralityWords } from "../profile/lateralityWords";
import type { Path } from "../lib/router";
import type { HubState } from "../data";
import type { AnswerValue, Answers, CompetitionType } from "../schema/types";
import { LIMITS } from "../schema/types";

/** The card's rhythm: a typing indicator before each of Lena's bubbles, a pause between two. */
export const TYPING_MS = 750;
export const BUBBLE_GAP_MS = 150;

/** The stepper's starting values on a first run (SPEC-18, Action list). */
const LOAD_DEFAULT = { trainings: 4, hours: 6 };

interface Message {
  id: number;
  from: "pyf" | "user";
  text: string;
}

/** loading → (asking ↔ speaking) → saving → done; stopped under the minimum age; failed when the save fails. */
type Phase = "loading" | "speaking" | "asking" | "stopped" | "saving" | "failed" | "done";

const questionOf = (id: string) => DADOS.questions.find((question) => question.id === id);

/** The screens she is asked, in order: hoursPerWeek rides on the trainings card, and a given first name is not asked. */
const screensOf = (items: Questionnaire) => screenIds(items) as Asked[];

/** One screen's answer: one card may give two (the load), and the laterality goes with its kind. */
type Entries = readonly (readonly [string, AnswerValue])[];

/** A quick reply: its words, what it stores, and whether it is her current answer. */
interface Chip {
  label: string;
  entries: Entries;
  pressed?: boolean;
}

/** Her earlier answer to this laterality question, on an edit: an option to preselect, or her own words. */
function earlierSide(defaults: Answers, question: LateralityQuestion): { option?: string; typed?: string } {
  const value = defaults.laterality;
  // An answer to another kind of question (her modality changed) is no answer to this one.
  if (typeof value !== "string" || defaults.lateralityKind !== question.kind) return {};
  return question.options.some((option) => option.id === value) ? { option: value } : { typed: value };
}

/** The laterality answer as stored: the kind first, so a cut between the two asks the question again. */
const sideEntries = (question: LateralityQuestion, value: string): Entries => [
  ["lateralityKind", question.kind],
  ["laterality", value],
];

/** What the composer holds when a question opens: her current value on an edit. */
function draftFor(question: Asked, defaults: Answers, current: Answers): string {
  if (question === "laterality") return earlierSide(defaults, lateralityQuestionOf(current)).typed ?? "";
  const value = defaults[question];
  if (typeof value !== "string") return "";
  return question === "birthDate" ? displayDate(value) : value;
}

function codeOf(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) return String(error.code);
  if (error instanceof Error) return error.name;
  return typeof error;
}

// A floor, not a height: a reply longer than the line wraps inside its pill. px-3, as in the
// SPEC-55 mockup: the three laterality replies of every kind but the guard fit one line at 390 px.
const chip =
  "min-h-[var(--control-h-sm)] max-w-full rounded-pill border px-3 py-2 text-body font-medium " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";
const wideButton =
  "flex h-[var(--control-h)] w-full items-center justify-center gap-2 rounded-pill text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";
const roundButton =
  "flex size-[var(--touch-min)] items-center justify-center rounded-pill border border-line-15 bg-white text-title " +
  "disabled:text-disabled";

export function ProfileOnboarding({
  onboarding,
  returnTo,
  typingMs = TYPING_MS,
  gapMs = BUBBLE_GAP_MS,
}: {
  onboarding: Onboarding;
  /** The screen that opened it and gets her back, Perfil for "Editar" (SPEC-17); the hub otherwise. */
  returnTo?: Path;
  typingMs?: number;
  gapMs?: number;
}) {
  const { navigate, back, leaveTo } = useRouter();
  const words = useCopy();
  const { language } = useLanguage();
  const side = useMemo(() => lateralityWords(language), [language]);
  const text = words.profileOnboarding;
  /** The words the conversation's async steps say: the language's at the moment Lena speaks. */
  const latest = useRef({ words, side });
  useEffect(() => {
    latest.current = { words, side };
  }, [words, side]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState(false);
  const [phase, setPhase] = useState<Phase>("loading");
  const [question, setQuestion] = useState<Asked | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [draft, setDraft] = useState("");
  const [other, setOther] = useState(false);
  const [load, setLoad] = useState(LOAD_DEFAULT);
  const [selected, setSelected] = useState<CompetitionType | null>(null);

  // What the async conversation reads between renders.
  const session = useRef({ accountId: "", editing: false, items: DADOS, defaults: {} as Answers, touched: false, shown: "" });
  /** Lena greets her before the first question, unless Conversa just did (read once, as the tab may change while it runs). */
  const [greets] = useState(() => returnTo !== undefined || originOf("dados") !== "conversa");
  /** Whether the team created the account, read once she is known to be signed in (SPEC-54). */
  const teamAccount = useRef<Promise<boolean>>(Promise.resolve(false));
  /** Bumped on unmount and on a restart, so a conversation in flight stops talking. */
  const generation = useRef(0);
  const nextId = useRef(0);
  const thread = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const say = (from: Message["from"], line: string) => {
    nextId.current += 1;
    const id = nextId.current;
    setMessages((shown) => [...shown, { id, from, text: line }]);
  };

  const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  /** PYF's bubbles one by one, each after the typing indicator. False when the conversation moved on meanwhile. */
  const speak = async (lines: readonly string[], gen: number): Promise<boolean> => {
    setPhase("speaking");
    for (const line of lines) {
      setTyping(true);
      await wait(typingMs);
      if (gen !== generation.current) return false;
      setTyping(false);
      say("pyf", line);
      await wait(gapMs);
      if (gen !== generation.current) return false;
    }
    return true;
  };

  const ask = async (next: Asked, current: Answers, gen: number) => {
    const { defaults, items } = session.current;
    const { words: spoken, side: spokenSide } = latest.current;
    const opening = greets && next === screensOf(items)[0];
    if (!(await speak(questionLines(next, current, localToday(), opening, spoken, spokenSide), gen))) return;
    setDraft(draftFor(next, defaults, current));
    setOther(false);
    setSelected(COMPETITION_TYPES.find((type) => type === defaults.competition) ?? null);
    setLoad({
      trainings: typeof defaults.trainingsPerWeek === "number" ? defaults.trainingsPerWeek : LOAD_DEFAULT.trainings,
      hours: typeof defaults.hoursPerWeek === "number" ? defaults.hoursPerWeek : LOAD_DEFAULT.hours,
    });
    setQuestion(next);
    setPhase("asking");
  };

  const finish = async (current: Answers, gen: number) => {
    setQuestion(null);
    setPhase("saving");
    const { accountId, editing } = session.current;
    try {
      const data = onboarding.data();
      await data.saveProfile(profileFromAnswers(current));
      // An edit keeps the run as it was finished, and with it the hub's date.
      if (!editing) await data.recordFinish(accountId, "dados");
    } catch (error) {
      // The code only: the profile is a minor's personal data.
      console.error("[dados] profile not saved", { code: codeOf(error) });
      if (await speak([latest.current.words.profileOnboarding.saveFailed], gen)) setPhase("failed");
      return;
    }
    if (await speak(latest.current.words.profileOnboarding.closing(firstNameOf(current)), gen)) setPhase("done");
  };

  const proceed = (current: Answers, gen: number) => {
    const next = resumeAt(session.current.items, current) as Asked | null;
    if (next) void ask(next, current, gen);
    else void finish(current, gen);
  };

  /** Stores her answer (one card may give two), shows her bubble, and moves on. */
  const answer = async (entries: Entries, shown: string) => {
    const gen = generation.current;
    const asked = question;
    session.current.touched = true;
    setQuestion(null);
    say("user", shown);
    const { accountId, editing, items } = session.current;
    const data = onboarding.data();
    let current = answers;
    try {
      for (const [questionId, value] of entries) {
        current = editing
          ? applyAnswer(items, current, questionId, value)
          : (await data.recordAnswer(accountId, "dados", questionId, value)).answers;
      }
    } catch (error) {
      console.error("[dados] answer not stored", { question: asked, code: codeOf(error) });
      if (asked && (await speak([latest.current.words.profileOnboarding.saveFailed], gen))) void ask(asked, answers, gen);
      return;
    }
    if (gen !== generation.current) return;
    setAnswers(current);
    proceed(current, gen);
  };

  const submitText = async (event: FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!question || value === "") return;
    if (question !== "birthDate") {
      // Her own words for her side, as typed, under the kind of question Lena asked.
      await answer(question === "laterality" ? sideEntries(lateralityQuestionOf(answers), value) : [[question, value]], value);
      return;
    }
    const gen = generation.current;
    // Closed while the claim is read, so a second tap cannot send the date twice.
    setQuestion(null);
    const minAge = (await teamAccount.current) ? LIMITS.TEAM_MIN_AGE_YEARS : LIMITS.MIN_AGE_YEARS;
    if (gen !== generation.current) return;
    const reading = readBirthDate(value, localToday(), minAge);
    if ("birthDate" in reading) {
      await answer([["birthDate", reading.birthDate]], displayDate(reading.birthDate));
      return;
    }
    say("user", value);
    if (reading.error === "unreadable") {
      if (await speak([text.dateUnreadable, text.birthDateAgain], gen)) {
        setDraft("");
        setQuestion("birthDate");
        setPhase("asking");
      }
      return;
    }
    // Under the minimum age: nothing of the date is stored, and the conversation ends here.
    if (await speak(text.tooYoung(firstNameOf(answers), minAge), gen)) setPhase("stopped");
  };

  useEffect(() => {
    let alive = true;

    /** Shows what she has answered at once, then asks the next question or saves. */
    const begin = (hub: HubState) => {
      const run = hub.assessments.find((entry) => entry.assessmentId === "dados")?.run ?? null;
      // What this conversation is built from; a refresh that changes none of it changes nothing.
      const shown = JSON.stringify([run?.state, run?.version, run?.answers, hub.profile]);
      if (shown === session.current.shown) return;
      generation.current += 1;
      const gen = generation.current;
      const editing = run?.state === "done";
      const accountFirstName = hub.profile?.firstName;
      const items = dadosConversation(editing, accountFirstName);
      const current: Answers = editing ? {} : (run?.answers ?? {});
      // Her stored profile is offered first: in an edit, and in the first run of an account
      // the team created, which holds her Dados gerais already (SPEC-54). After Criar conta
      // it holds the first name only, which a first run does not ask.
      let defaults: Answers = {};
      if (hub.profile) defaults = answersFromProfile(hub.profile);
      else if (editing) defaults = run.answers;
      session.current = { accountId: hub.accountId, editing, items, defaults, touched: false, shown };

      const history: Message[] = [];
      const today = localToday();
      const screens = screensOf(items);
      const { words: spoken, side: spokenSide } = latest.current;
      for (const id of screens) {
        if (current[id] === undefined) continue;
        for (const line of questionLines(id, current, today, greets && id === screens[0], spoken, spokenSide)) {
          history.push({ id: history.length + 1, from: "pyf", text: line });
        }
        history.push({ id: history.length + 1, from: "user", text: answerLine(id, current, spoken, spokenSide) });
      }
      nextId.current = history.length;
      setMessages(history);
      setTyping(false);
      setQuestion(null);
      setAnswers(current);

      // A first run carries the name from Criar conta into the run before
      // the first question, so the answers the run finishes with are whole.
      if (editing || current.firstName !== undefined || !accountFirstName || items !== DADOS) {
        proceed(current, gen);
        return;
      }
      onboarding
        .data()
        .recordAnswer(hub.accountId, "dados", "firstName", accountFirstName)
        .then((carried) => {
          if (gen !== generation.current) return;
          setAnswers(carried.answers);
          proceed(carried.answers, gen);
        })
        .catch((error: unknown) => {
          // The device did not store it: ask the name, which her profile offers, and whose answer goes through the usual retry.
          console.error("[dados] first name not carried", { code: codeOf(error) });
          if (gen !== generation.current) return;
          session.current.items = DADOS_ASKING_NAME;
          proceed(current, gen);
        });
    };

    const start = async () => {
      const accountId = await onboarding.accountId();
      if (!alive) return;
      if (!accountId) {
        navigate(paths.entry, { replace: true });
        return;
      }
      // Read alongside the hub, awaited only when she gives her birth date.
      teamAccount.current = onboarding.createdByTeam().catch((error: unknown) => {
        // Unread (an expired token offline): the default minimum age, which the server applies too.
        console.error("[dados] account origin not read", { code: codeOf(error) });
        return false;
      });
      const hub = await onboarding.data().loadHub(accountId, (fresh) => {
        // The server's state replaces the device's only before she answers here,
        // and only when it differs: another device got further, or finished.
        if (alive && !session.current.touched) begin(fresh);
      });
      if (alive) begin(hub);
    };
    start().catch((error: unknown) => {
      console.error("[dados] could not open", { code: codeOf(error) });
      if (alive) navigate(paths.entry, { replace: true });
    });

    return () => {
      alive = false;
      generation.current += 1;
    };
    // Runs once per visit; begin and proceed read the latest state through refs.
  }, []);

  // The list always shows its latest line.
  useEffect(() => {
    const element = thread.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages, typing, phase]);

  useEffect(() => {
    if (other) input.current?.focus();
  }, [other]);

  const { answered, total } = progress(session.current.items, answers);
  const counter = phase === "done" ? total : Math.min(answered + 1, total);
  const barShare = phase === "done" ? 1 : answered / total;
  // Back down the history to the screen that opened it, so its ← leads where it did before.
  const origin = originOf("dados");
  const leave = () => (returnTo ? back(returnTo) : leaveTo(originPath(origin)));

  const asking = phase === "asking" ? question : null;
  const composer = asking !== null && asking !== "trainingsPerWeek" && asking !== "competition";
  /** Her value as the chip says it: the sport and the level are stored in the words she taps. */
  const valueChips = (id: Asked, labels: readonly string[]): Chip[] => labels.map((label) => ({ label, entries: [[id, label]] }));
  let chips: readonly Chip[] = [];
  let otherLabel: string | null = null;
  let placeholder: string = text.orWriteHere;
  switch (asking) {
    case "firstName":
      placeholder = text.firstNamePlaceholder;
      break;
    case "birthDate":
      placeholder = text.birthDatePlaceholder;
      break;
    case "modality":
      chips = valueChips("modality", text.modalityChips);
      otherLabel = text.modalityOther;
      if (other) placeholder = text.modalityOtherPlaceholder;
      break;
    case "position":
      placeholder = text.positionPlaceholder;
      break;
    case "laterality": {
      const sideQuestion = lateralityQuestionOf(answers);
      const { option: earlier } = earlierSide(session.current.defaults, sideQuestion);
      chips = sideQuestion.options.map((option) => ({
        label: side.option(sideQuestion.kind, option.id),
        entries: sideEntries(sideQuestion, option.id),
        pressed: option.id === earlier,
      }));
      break;
    }
    case "level":
      chips = valueChips("level", text.levelChips);
      otherLabel = text.levelOther;
      if (other) placeholder = text.levelOtherPlaceholder;
      break;
    case "club":
      // « Sem clube » is the empty club.
      chips = [{ label: text.noClub, entries: [["club", ""]] }];
      placeholder = text.clubPlaceholder;
      break;
  }
  // The date is read whatever its spacing; every other answer is capped where the lib caps it.
  const maxLength = asking && asking !== "birthDate" ? questionOf(asking)?.maxLength : undefined;
  const trainings = questionOf("trainingsPerWeek");
  const hours = questionOf("hoursPerWeek");

  return (
    <main className="screen-enter flex h-dvh flex-col bg-cream">
      <header className="px-5 pt-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={leave}
            className="flex size-[var(--touch-min)] items-center justify-center rounded-pill text-title text-dark transition-colors duration-[var(--dur-micro)] ease-pyf hover:bg-cream-deep"
          >
            <span aria-hidden="true">←</span>
            <span className="sr-only">{returnTo ? text.leaveEdit : text.back}</span>
          </button>
          <PyfTitle />
          <span data-testid="counter" className="min-w-[var(--touch-min)] text-right text-body-sm font-medium text-muted">
            {counter}/{total}
          </span>
        </div>
        <p className="pt-3 text-label font-semibold tracking-[var(--ls-label)] text-muted uppercase">{text.part}</p>
        <div
          role="progressbar"
          aria-label={text.part}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={phase === "done" ? total : answered}
          className="mt-2 h-1 overflow-hidden rounded-pill bg-line-10"
        >
          {/* A transform, not a width: the bar slides without a layout pass, and holds still under reduced motion. */}
          <div
            className="h-full w-full rounded-pill bg-lime transition-transform duration-[var(--dur-step)] ease-pyf motion-reduce:transition-none"
            style={{ transform: `translateX(${(barShare - 1) * 100}%)` }}
          />
        </div>
      </header>

      <section ref={thread} aria-live="polite" className="flex flex-1 flex-col gap-2 overflow-y-auto px-5 py-4">
        {messages.map((message) =>
          message.from === "pyf" ? (
            <PyfBubble key={message.id}>{message.text}</PyfBubble>
          ) : (
            <UserBubble key={message.id}>{message.text}</UserBubble>
          ),
        )}
        {typing && <TypingBubble label={text.typing} />}
        {phase === "done" && (
          <section aria-label={text.summary.title} className="bubble-enter mt-2 rounded-card bg-white p-5 shadow-float">
            <p className="text-label font-semibold tracking-[var(--ls-label)] text-lime-ink uppercase">
              <span aria-hidden="true">✓ </span>
              {text.summary.title}
            </p>
            <dl className="mt-3 flex flex-col">
              {summaryRows(answers, localToday(), words, side).map((row) => (
                <div key={row.label} className="flex justify-between gap-4 border-t border-line-8 py-2 first:border-t-0">
                  <dt className="text-body-sm text-muted">{row.label}</dt>
                  <dd className="min-w-0 text-right text-body-sm font-medium break-words">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </section>

      <footer className="flex flex-col gap-3 px-5 pb-5">
        {chips.length > 0 && !other && (
          <div className="flex flex-wrap justify-end gap-2">
            {chips.map(({ label, entries, pressed }) => (
              <button
                key={label}
                type="button"
                aria-pressed={pressed}
                onClick={() => void answer(entries, label)}
                className={`${chip} ${pressed ? "border-violet bg-violet-tint text-violet-ink" : "border-line-15 bg-white text-dark hover:bg-cream-deep"}`}
              >
                {label}
              </button>
            ))}
            {otherLabel && (
              <button
                type="button"
                onClick={() => setOther(true)}
                className={`${chip} border-violet-tint bg-violet-tint font-semibold text-violet-ink`}
              >
                {otherLabel}
              </button>
            )}
          </div>
        )}

        {composer && (
          <form onSubmit={submitText} className="flex items-center gap-3">
            <input
              ref={input}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={maxLength}
              placeholder={placeholder}
              aria-label={placeholder}
              autoCapitalize={asking === "firstName" ? "words" : "sentences"}
              className={
                "h-[var(--composer-h)] min-w-0 flex-1 rounded-pill border border-line-10 bg-white px-5 text-body-lg text-dark " +
                "outline-none placeholder:text-muted focus:border-violet"
              }
            />
            <button
              type="submit"
              disabled={draft.trim() === ""}
              className="flex size-[var(--composer-h)] shrink-0 items-center justify-center rounded-pill bg-violet text-title text-white transition-colors duration-[var(--dur-micro)] ease-pyf hover:bg-violet-ink disabled:bg-line-10 disabled:text-disabled"
            >
              <span aria-hidden="true">↑</span>
              <span className="sr-only">{text.send}</span>
            </button>
          </form>
        )}

        {asking === "trainingsPerWeek" && trainings && hours && (
          <div className="rounded-card bg-white p-5 shadow-option">
            {(
              [
                ["trainings", text.trainings, trainings.min ?? 0, trainings.max ?? 0],
                ["hours", text.hours, hours.min ?? 0, hours.max ?? 0],
              ] as const
            ).map(([key, label, min, max]) => (
              <div key={key} role="group" aria-label={label} className="flex items-center justify-between py-2">
                <span className="text-body font-medium">{label}</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={load[key] <= min}
                    onClick={() => setLoad((now) => ({ ...now, [key]: Math.max(min, now[key] - 1) }))}
                    className={roundButton}
                  >
                    <span aria-hidden="true">−</span>
                    <span className="sr-only">{text.less}</span>
                  </button>
                  <output className="w-8 text-center text-title font-medium">{load[key]}</output>
                  <button
                    type="button"
                    disabled={load[key] >= max}
                    onClick={() => setLoad((now) => ({ ...now, [key]: Math.min(max, now[key] + 1) }))}
                    className={roundButton}
                  >
                    <span aria-hidden="true">+</span>
                    <span className="sr-only">{text.more}</span>
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                void answer(
                  [
                    // Hours first: cut between the two, the trainings card is asked again.
                    ["hoursPerWeek", load.hours],
                    ["trainingsPerWeek", load.trainings],
                  ],
                  text.loadAnswer(load.trainings, load.hours),
                )
              }
              className={`${wideButton} mt-3 bg-dark text-cream hover:bg-dark-hover`}
            >
              {text.confirm}
            </button>
          </div>
        )}

        {asking === "competition" && (
          <div className="flex flex-col gap-3">
            {COMPETITION_TYPES.map((type) => {
              const option = text.competitionOptions[type];
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={selected === type}
                  onClick={() => void answer([["competition", type]], option.title)}
                  className={
                    "flex items-center gap-4 rounded-card-inner border-[1.5px] p-4 text-left shadow-option " +
                    (selected === type ? "border-violet bg-violet-tint" : "border-transparent bg-white")
                  }
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-pill bg-violet-tint font-semibold text-violet-ink">
                    {option.letter}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-body font-semibold">{option.title}</span>
                    <span className="text-body-sm text-muted">{option.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {phase === "stopped" && (
          <button type="button" onClick={leave} className={`${wideButton} border border-line-20 text-dark hover:bg-cream-deep`}>
            {text.leave}
          </button>
        )}
        {phase === "failed" && (
          <button
            type="button"
            onClick={() => void finish(answers, generation.current)}
            className={`${wideButton} bg-dark text-cream hover:bg-dark-hover`}
          >
            {text.retry}
          </button>
        )}
        {phase === "done" && returnTo && (
          <button type="button" onClick={leave} className={`${wideButton} bg-dark text-cream hover:bg-dark-hover`}>
            {text.toProfile}
            <span aria-hidden="true">→</span>
          </button>
        )}
        {phase === "done" && !returnTo && (
          <div className="flex flex-wrap justify-end gap-2">
            {waysOut(origin).map((to) => (
              <button
                key={to}
                type="button"
                onClick={() => leaveTo(to)}
                className={`${chip} border-line-15 bg-white text-dark hover:bg-cream-deep`}
              >
                {to === paths.conversa ? text.toConversa : text.toAssessments}
              </button>
            ))}
          </div>
        )}
      </footer>
    </main>
  );
}
